import {ReactNode, Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {CheckBoxGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";

import {validationMessages} from "../../../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {useAppSelector} from "../../../../store/hooks";
import {gtmCheckout} from "../../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../../../tracking/google_tag_manager/gtm_product_add";
import {isValidationError} from "../../../../utils/isValidationError";
import {getCheckboxConfigBySource} from "../../../constants/lead/lead_modal_checkbox_options";
import {useSendMainLead} from "../../../hooks/use_send_main_lead";
import {IApplicationFormValues} from "../../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../../types/IApplicationData";
import {getRodoDataFromApplication} from "../../../utils/lead/get_rodo_data_from_application";
import {normalizeLeadQuestions} from "../../../utils/lead/normalize_lead_questions";
import {LeadModalFormSubmitFooter} from "../LeadModalFormSubmitFooter";
import {LeadModalUserContactDataForm} from "../LeadModalUserContactDataForm";

const formSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    }),
    userMessage: string().when("questions", {
        is: (questions: string[]) => questions.some((q) => q === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
    questions: string[];
    userMessage: string;
}

interface IProps {
    handleModalClose: (forceClose?: boolean) => void;
    onSuccessfulSubmit: (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    storeHash?: string;
    isTwoColumnGrid?: boolean;
    initialLeadFormValues: IApplicationFormValues | null;
    offerId: number | null;
    propertyId: number | null;
    customHeader: ReactNode;
}

export const AskForFullOfferForm = (props: IProps) => {
    const [isSending, setIsSending] = useState(false);

    const {handleModalClose, onSuccessfulSubmit, isTwoColumnGrid, storeHash, initialLeadFormValues, offerId, customHeader} = props;
    const {email, marketing_email, marketing_sms, name, phone} = initialLeadFormValues || {};

    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useAppSelector((state) => state.application[storeHash ?? ""]);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData);

    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const wasInteractionHitRef = useRef(false);

    const defaultValues: IDefaultValues = {
        name: name || "",
        email: email || "",
        phone: phone || {code: 48, number: ""},
        marketing_email: marketing_email || false,
        marketing_sms: marketing_sms || false,
        questions: [],
        userMessage: ""
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit, watch} = formMethods;
    const selectedQuestions = watch("questions") || [];

    const otherOptionSelected = selectedQuestions.some((q) => q === "inny");
    const checkboxConfig = getCheckboxConfigBySource(leadModalState.source);
    const shouldShowAllOptions = checkboxConfig.showAllOptions || showMoreOptions;
    const combinedOptions = shouldShowAllOptions ? [...checkboxConfig.baseOptions, ...checkboxConfig.moreOptions] : checkboxConfig.baseOptions;

    const sendLead = useSendMainLead();

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && leadModalState.sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, leadModalState.sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && leadModalState.sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, leadModalState.sourceSection);

            const {name, email, phone} = initialLeadFormValues || {};
            if (name || email || phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, leadModalState.sourceSection, applicationData]);

    const onSubmit = async (values: IDefaultValues) => {
        const formValues = {...defaultValues, ...values, additionalQuestions: normalizeLeadQuestions(combinedOptions, values.questions, values.userMessage)};

        setIsSending(true);

        try {
            const application = (await sendLead(formValues, handleModalClose)) as unknown as IApplicationData;

            await onSuccessfulSubmit(application, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as keyof IDefaultValues, {message: errorMessage.join(", ")});
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <div className={formWrapperStyles}>
            <Suspense fallback={<Loader />}>
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                        {customHeader}

                        <LeadModalUserContactDataForm />

                        <Text variant="body_copy_2" mb={2}>
                            Chcę zapytać o:
                        </Text>

                        <CheckBoxGroupControlled
                            name="questions"
                            options={combinedOptions}
                            checkBoxClassName={css({alignItems: "flex-start"})}
                            checkboxListClassName={isTwoColumnGrid ? checkboxGrid : undefined}
                        />

                        {!checkboxConfig.showAllOptions && (
                            <Text className={showMoreOptionsTextStyles} variant="info_txt_1" strong mb={2} onClick={() => setShowMoreOptions(!showMoreOptions)}>
                                {showMoreOptions ? "Mniej tematów" : "Więcej tematów"}
                            </Text>
                        )}

                        {shouldShowAllOptions && otherOptionSelected && (
                            <TextareaField className={css({mb: 3})} name="userMessage" placeholder="Wpisz własną wiadomość do dewelopera." />
                        )}

                        <div className={formFooterStyles}>
                            <LeadModalFormSubmitFooter
                                isSending={isSending}
                                buttonText="Wyślij"
                                vendorName={vendorName}
                                offerPersonalDataProcessorName={offerPersonalDataProcessorName}
                                rodoLinks={rodoLinks}
                            />
                        </div>
                    </form>
                </FormProvider>
            </Suspense>
        </div>
    );
};

const checkboxGrid = css({
    md: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "0.5rem 1rem"
    }
});

const showMoreOptionsTextStyles = css({
    textDecoration: "underline",
    cursor: "pointer"
});

const formWrapperStyles = css({
    h: "100%",
    md: {
        h: "auto"
    }
});

const formFooterStyles = css({
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    pt: 2,
    backgroundColor: "#fff",
    _before: {
        content: '""',
        position: "absolute",
        top: "-24px",
        left: 0,
        right: 0,
        h: "24px",
        background: "linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
        pointerEvents: "none"
    },
    md: {
        position: "static",
        pt: 0,
        backgroundColor: "transparent",
        _before: {
            display: "none"
        }
    }
});
import {ReactNode, Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {CheckBoxGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";

import {validationMessages} from "../../../../form/utils/validation_messages";
import {hitGTMEcommerceAddToCart} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_add_to_cart";
import {hitGTMEcommerceBeginCheckout} from "../../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetailApplication} from "../../../../offer/types/IOfferDetailApplication";
import {IPropertyDetailApplication} from "../../../../property/types/IPropertyDetailApplication";
import {useAppSelector} from "../../../../store/hooks";
import {gtmCheckout} from "../../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../../tracking/google_tag_manager/gtm_event_form_open";
import {gtmProductAdd} from "../../../../tracking/google_tag_manager/gtm_product_add";
import {isValidationError} from "../../../../utils/isValidationError";
import {getCheckboxConfigBySource} from "../../../constants/lead/lead_modal_checkbox_options";
import {useSendMainLead} from "../../../hooks/use_send_main_lead";
import {IApplicationFormValues} from "../../../types/form/IApplicationFormValues";
import {IApplicationData} from "../../../types/IApplicationData";
import {getRodoDataFromApplication} from "../../../utils/lead/get_rodo_data_from_application";
import {normalizeLeadQuestions} from "../../../utils/lead/normalize_lead_questions";
import {LeadModalFormSubmitFooter} from "../LeadModalFormSubmitFooter";
import {LeadModalUserContactDataForm} from "../LeadModalUserContactDataForm";

const formSchema = object().shape({
    email: string().email().required(validationMessages.email),
    name: string().required(validationMessages.required),
    phone: object().shape({
        code: number().required(validationMessages.required),
        number: string().required(validationMessages.required)
    }),
    userMessage: string().when("questions", {
        is: (questions: string[]) => questions.some((q) => q === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    marketing_email: boolean;
    marketing_sms: boolean;
    questions: string[];
    userMessage: string;
}

interface IProps {
    handleModalClose: (forceClose?: boolean) => void;
    onSuccessfulSubmit: (
        sentApplication: IApplicationData | null,
        offer?: IOfferDetailApplication,
        property?: IPropertyDetailApplication,
        recommendedFilters?: Record<string, string>
    ) => Promise<void>;
    storeHash?: string;
    isTwoColumnGrid?: boolean;
    initialLeadFormValues: IApplicationFormValues | null;
    offerId: number | null;
    propertyId: number | null;
    customHeader: ReactNode;
}

export const AskForFullOfferForm = (props: IProps) => {
    const [isSending, setIsSending] = useState(false);

    const {handleModalClose, onSuccessfulSubmit, isTwoColumnGrid, storeHash, initialLeadFormValues, offerId, customHeader} = props;
    const {email, marketing_email, marketing_sms, name, phone} = initialLeadFormValues || {};

    const leadModalState = useAppSelector((state) => state.mainLead);
    const applicationData = useAppSelector((state) => state.application[storeHash ?? ""]);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadType = useAppSelector((state) => state.mainLead.mainLeadType);
    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData);

    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const wasInteractionHitRef = useRef(false);

    const defaultValues: IDefaultValues = {
        name: name || "",
        email: email || "",
        phone: phone || {code: 48, number: ""},
        marketing_email: marketing_email || false,
        marketing_sms: marketing_sms || false,
        questions: [],
        userMessage: ""
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (applicationData?.vendor?.detail?.name || applicationData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (applicationData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit, watch} = formMethods;
    const selectedQuestions = watch("questions") || [];

    const otherOptionSelected = selectedQuestions.some((q) => q === "inny");
    const checkboxConfig = getCheckboxConfigBySource(leadModalState.source);
    const shouldShowAllOptions = checkboxConfig.showAllOptions || showMoreOptions;
    const combinedOptions = shouldShowAllOptions ? [...checkboxConfig.baseOptions, ...checkboxConfig.moreOptions] : checkboxConfig.baseOptions;

    const sendLead = useSendMainLead();

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && mainLeadType && leadModalState.sourceSection && applicationData) {
            wasInteractionHitRef.current = true;

            gtmCheckout(applicationData, viewType, mainLeadType);
            hitGTMEcommerceBeginCheckout(applicationData, viewType, mainLeadType, leadModalState.sourceSection, formFillAction);
        }
    };

    // Track form opening and autofill interaction
    useEffect(() => {
        if (mainLeadType && leadModalState.sourceSection && applicationData) {
            gtmProductAdd(applicationData, viewType, mainLeadType);
            hitGTMEcommerceAddToCart(applicationData, viewType, mainLeadType, leadModalState.sourceSection);

            const {name, email, phone} = initialLeadFormValues || {};
            if (name || email || phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [mainLeadType, leadModalState.sourceSection, applicationData]);

    const onSubmit = async (values: IDefaultValues) => {
        const formValues = {...defaultValues, ...values, additionalQuestions: normalizeLeadQuestions(combinedOptions, values.questions, values.userMessage)};

        setIsSending(true);

        try {
            const application = (await sendLead(formValues, handleModalClose)) as unknown as IApplicationData;

            await onSuccessfulSubmit(application, applicationData?.offer?.detail, applicationData?.property?.detail);
        } catch (error) {
            if (isValidationError(error)) {
                const fieldErrors = error.appError.fieldErrors;

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    formMethods.setError(fieldName as keyof IDefaultValues, {message: errorMessage.join(", ")});
                }
            }
        } finally {
            setIsSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <div className={formWrapperStyles}>
            <Suspense fallback={<Loader />}>
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                        {customHeader}

                        <LeadModalUserContactDataForm />

                        <Text variant="body_copy_2" mb={2}>
                            Chcę zapytać o:
                        </Text>

                        <CheckBoxGroupControlled
                            name="questions"
                            options={combinedOptions}
                            checkBoxClassName={css({alignItems: "flex-start"})}
                            checkboxListClassName={isTwoColumnGrid ? checkboxGrid : undefined}
                        />

                        {!checkboxConfig.showAllOptions && (
                            <Text className={showMoreOptionsTextStyles} variant="info_txt_1" strong mb={2} onClick={() => setShowMoreOptions(!showMoreOptions)}>
                                {showMoreOptions ? "Mniej tematów" : "Więcej tematów"}
                            </Text>
                        )}

                        {shouldShowAllOptions && otherOptionSelected && (
                            <TextareaField className={css({mb: 3})} name="userMessage" placeholder="Wpisz własną wiadomość do dewelopera." />
                        )}

                        <div className={formFooterStyles}>
                            <LeadModalFormSubmitFooter
                                isSending={isSending}
                                buttonText="Wyślij"
                                vendorName={vendorName}
                                offerPersonalDataProcessorName={offerPersonalDataProcessorName}
                                rodoLinks={rodoLinks}
                            />
                        </div>
                    </form>
                </FormProvider>
            </Suspense>
        </div>
    );
};

const checkboxGrid = css({
    md: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "0.5rem 1rem"
    }
});

const showMoreOptionsTextStyles = css({
    textDecoration: "underline",
    cursor: "pointer"
});

const formWrapperStyles = css({
    h: "100%",
    md: {
        h: "auto"
    }
});

const formFooterStyles = css({
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    pt: 2,
    backgroundColor: "#fff",
    _before: {
        content: '""',
        position: "absolute",
        top: "-24px",
        left: 0,
        right: 0,
        h: "24px",
        background: "linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
        pointerEvents: "none"
    },
    md: {
        position: "static",
        pt: 0,
        backgroundColor: "transparent",
        _before: {
            display: "none"
        }
    }
});
